import { DefectSelection } from "../DefectSelection";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InspectorContext } from "../../reducer";
import { InspectorRow } from "@/api/hitachi/rbi";
import { MinMaxInput } from "@/components";
import { MultiSelectionDialog } from "@/components/dialogs";
import { useAcceptRejectAll, useHandleFieldChanged } from "../../hooks";
import { useContext } from "react";
import { useInventory } from "../../queries";
import { useModal } from "@/hooks";
import { ProbeSelection } from "../ProbeSelection";
import { useCoordinatorMenu } from "@/queries/menu.queries";

export function ET() {
  const { state, updateState } = useContext(InspectorContext);

  const equipment = useInventory("ETTestEquipment");
  const surfaceConditions = useCoordinatorMenu("SurfaceCondition");

  const modal = useModal();
  const acceptReject = useAcceptRejectAll();
  const handleFieldChanged = useHandleFieldChanged();

  function normalizeCopiedValue(field: keyof InspectorRow, value?: string | string[]) {
    if (!value || Array.isArray(value)) {
      return value;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return value;
    }
    if (field === "surfaceTemp") {
      return `${Math.min(50, Math.max(10, numericValue))}`;
    }
    return value;
  }

  function handleCopy(field: keyof InspectorRow, value?: string | string[]) {
    const normalizedValue = normalizeCopiedValue(field, value);
    updateState({
      type: "Inspections",
      payload: state.inspections.map((process) => {
        const copiedValue = Array.isArray(normalizedValue) ? [...normalizedValue] : normalizedValue;
        return { ...process, [field]: copiedValue };
      }),
    });
  }

  return (
    <>
      <thead>
        <tr>
          <th>Weld Map</th>
          <th>Drawing No.</th>
          <th>Weld ID No.</th>
          <th>Work Instruction</th>
          <th>Joint Type</th>
          <th>Defect Acceptance Level</th>
          <th>Surface Temp. (°C)</th>
          {state.inspections.length > 1 && <th />}
          <th>Surface Condition</th>
          {state.inspections.length > 1 && <th />}
          <th>Test Equipment</th>
          {state.inspections.length > 1 && <th />}
          <th>Probes</th>
          <th>Accept</th>
          <th>Reject</th>
          <th>Recordable Indications</th>
        </tr>
      </thead>
      <tbody>
        {state.inspections.map((row, index) => (
          <tr key={index}>
            <td>{row.weldMap}</td>
            <td>{row.drawingNumber}</td>
            <td>{row.weldNumber}</td>
            <td>{row.workInstruction}</td>
            <td>{row.jointType}</td>
            <td>{row.acceptanceLevel}</td>
            <td>
              <MinMaxInput
                min={10}
                max={50}
                type="text"
                className="form-control"
                value={row.surfaceTemp ?? ""}
                onChange={(e) => handleFieldChanged(index, "surfaceTemp", e.currentTarget.value)}
              />
            </td>
            {state.inspections.length > 1 && (
              <td className="p-0 pt-2">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("surfaceTemp", row.surfaceTemp)}
                  />
                )}
              </td>
            )}
            <td>
              <select
                className="form-select"
                value={row.surfaceCondition ?? ""}
                onChange={(e) => handleFieldChanged(index, "surfaceCondition", e.currentTarget.value)}
              >
                <option></option>
                {surfaceConditions.data?.map((condition, index) => (
                  <option key={index}>{condition}</option>
                ))}
              </select>
            </td>
            {state.inspections.length > 1 && (
              <td className="p-0 pt-2">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("surfaceCondition", row.surfaceCondition)}
                  />
                )}
              </td>
            )}
            <td>
              <button
                role="link"
                className="btn btn-link"
                onClick={() =>
                  modal.show(
                    <MultiSelectionDialog
                      caption="Test Equipment"
                      options={equipment.data?.map((x) => x.serialNumber)}
                      selected={row.equipmentNumber}
                      onAccept={(values) => handleFieldChanged(index, "equipmentNumber", values)}
                    />
                  )
                }
              >
                {row.equipmentNumber.length === 0 && "Click to select"}
                {row.equipmentNumber.map((value) => (
                  <div key={value}>{value}</div>
                ))}
              </button>
            </td>
            {state.inspections.length > 1 && (
              <td className="p-0 pt-2">
                {index === 0 && (
                  <FontAwesomeIcon
                    icon={faArrowDown}
                    size="sm"
                    type="button"
                    title="Copy"
                    onClick={() => handleCopy("equipmentNumber", row.equipmentNumber)}
                  />
                )}
              </td>
            )}
            <td>
              <button
                role="link"
                className="btn btn-link"
                onClick={() =>
                  modal.show(
                    <ProbeSelection row={row} onAccept={(probes) => handleFieldChanged(index, "probes", probes)} />
                  )
                }
              >
                {row.probes?.length > 0 ? `${row.probes?.length} Selected` : "Click to select"}
              </button>
            </td>
            <td>
              <input
                type="radio"
                className="form-check-input"
                name={`result${index}`}
                checked={row.accept === true}
                onChange={() => handleFieldChanged(index, "accept", true)}
                onClick={(e) => e.currentTarget.checked && handleFieldChanged(index, "accept", undefined)}
                disabled={!row.surfaceTemp || row.equipmentNumber.length === 0 || row.probes.length === 0}
              />
            </td>
            <td>
              <input
                type="radio"
                className="form-check-input"
                name={`result${index}`}
                checked={row.accept === false}
                onChange={() => handleFieldChanged(index, "accept", false)}
                onClick={(e) => e.currentTarget.checked && handleFieldChanged(index, "accept", undefined)}
                disabled={!row.surfaceTemp || row.equipmentNumber.length === 0 || row.probes.length === 0}
              />
            </td>
            <td>
              <button
                role="link"
                className="btn btn-link"
                disabled={row.accept !== false}
                onClick={() =>
                  modal.show(
                    <DefectSelection
                      caption="Recordable Indications"
                      weld={row}
                      onAccept={(defects) => handleFieldChanged(index, "defects", defects)}
                    />
                  )
                }
              >
                {row.defects?.length > 0 ? `${row.defects?.length} Selected` : "Click to select"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>

      {state.inspections.length > 1 && (
        <tfoot>
          <tr>
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            {state.inspections.length > 1 && <td />}
            <td />
            <td />
            <td>
              <button type="button" className="btn btn-default btn-sm" onClick={acceptReject.acceptAll}>
                Accept all
              </button>
            </td>
            <td>
              <button type="button" className="btn btn-default btn-sm" onClick={acceptReject.rejectAll}>
                Reject All
              </button>
            </td>
            <td />
          </tr>
        </tfoot>
      )}
    </>
  );
}
